/**
 * realtimeIdleSocket — close the realtime websocket once nothing is using it.
 *
 * THE DEFECT
 * ----------
 * Phoenix keeps the socket AND its reconnect timer alive after the last channel
 * is removed. Nothing in this app ever tore that socket down:
 * `supabase.realtime.disconnect()` had exactly one call site
 * (realtimeReconnect.ts's force-cycle), and it is never reached by a channel
 * simply draining to zero or by sign-out.
 *
 * So a user who signs out, or lands on a route that needs no realtime, leaves a
 * socket that serves nothing and — on a device whose transport is failing —
 * retries on phoenix's capped backoff (1s, 2s, 5s, then 10s) for as long as the
 * app stays open. It costs radio and battery, and it is a measurable share of
 * the realtime error volume.
 *
 * MEASURED, not assumed (live PostHog, 14 days to 2026-08-22):
 *   socket-close   with channelCount 0 — 26 events across 20 users
 *   transport-dead with channelCount 0 — 15 events across 13 users
 * i.e. 41 of 170 close events (24%) reported a socket with NOTHING joined, and
 * they touch MORE distinct users (20/13) than the 1+-channel cases do (5). Still
 * reproducing on v1.1.2 at `/login` with channelCount 0 as recently as
 * 2026-08-21.
 *
 * Note this is a separate problem from the 1006 closes themselves (CLAUDE.md
 * §33), which are a transport-level failure below the app and are NOT fixable in
 * JS. This does not try to fix those. It removes the pointless retries that
 * serve no channel.
 *
 * WHY A POLL AND NOT A HOOK ON REMOVAL
 * ------------------------------------
 * There are 46 `supabase.channel(...)` creation sites and 52 removal sites
 * across 35 files. Editing every one, or monkey-patching `removeChannel`, would
 * be a far larger and more fragile change than the defect warrants — and this
 * codebase has already been bitten twice by reaching into realtime-js internals
 * that later moved (`supabase.realtime.conn` silently became a no-op guard, and
 * the socket telemetry it gated never fired for 60 days).
 *
 * A low-frequency poll reads only `channels`, `isConnected()` and `disconnect()`
 * — all long-standing public surface that `nativeRealtimeLifecycle.channelCount()`
 * and `realtimeReconnect` already depend on and that are proven working by live
 * telemetry (channelCount reports real 0 / 4-8 values today).
 *
 * WHY THE IDLE GRACE PERIOD IS THE WHOLE SAFETY ARGUMENT
 * -----------------------------------------------------
 * The obvious risk is thrash: a route change unmounts several hooks and mounts
 * several more, so the channel count legitimately touches zero for a moment
 * mid-navigation. Disconnecting on that instant would trade a wasteful socket
 * for a disconnect/reconnect storm — strictly worse on the exact devices this is
 * meant to help.
 *
 * So zero is not the trigger; zero *sustained for IDLE_GRACE_MS* is. Navigation
 * gaps are milliseconds. 30 seconds is three orders of magnitude clear of that,
 * while still bounding a dead retry loop to well under a minute instead of the
 * lifetime of the app.
 *
 * VERIFIED AGAINST THE INSTALLED SDK (@supabase/realtime-js 2.110.1 +
 * @supabase/phoenix), not assumed:
 *   - `socket.disconnect()` calls `reconnectTimer.reset()` and sets
 *     `closeWasClean = true`; `onConnClose` only calls
 *     `reconnectTimer.scheduleTimeout()` `if(!this.closeWasClean)`. An explicit
 *     disconnect therefore genuinely STOPS the retry loop rather than triggering
 *     another cycle.
 *   - `RealtimeChannel.subscribe()` opens with
 *     `if (!this.socket.isConnected()) { this.socket.connect(); }`, so the next
 *     real subscription re-establishes the socket on its own. Nothing has to
 *     re-connect it manually, and no call site changes.
 *   - `phoenix.teardown()` does `if(code){ conn.close(code, reason) } else {
 *     conn.close() }`. A bare `close()` makes the browser emit **1005**, which
 *     `nativeRealtimeLifecycle`'s onClose counts as an ABNORMAL close — this fix
 *     would then manufacture the very telemetry it is meant to remove. Hence the
 *     explicit 1000 below; that path returns early and resets the streak.
 */
import { supabase } from "@/integrations/supabase/client";
import { isForcedReconnectSuppressed } from "@/lib/realtimeReconnect";

/** How often to sample the channel count. Cheap: two property reads. */
const POLL_INTERVAL_MS = 10_000;

/**
 * How long the socket must be continuously channel-less before it is closed.
 * See the thrash argument above — this is the guard that makes the fix safe on
 * navigation, so do not lower it without re-checking that route changes cannot
 * hold zero channels for this long.
 */
const IDLE_GRACE_MS = 30_000;

/** Normal closure. Deliberate: a bare close() would surface as 1005/abnormal. */
const CLOSE_CODE_NORMAL = 1000;

let timer: ReturnType<typeof setInterval> | null = null;
/** When the channel count was first observed at zero, or null if non-zero. */
let zeroSince: number | null = null;

interface RealtimeLike {
  channels?: unknown[];
  isConnected?: () => boolean;
  isConnecting?: () => boolean;
  isDisconnecting?: () => boolean;
  disconnect?: (code?: number, reason?: string) => unknown;
}

function realtime(): RealtimeLike {
  return supabase.realtime as unknown as RealtimeLike;
}

/**
 * Number of channels currently registered on the client, or -1 if unreadable.
 * -1 is treated as "unknown, do nothing" rather than as zero — if a future SDK
 * moves this field, the correct failure mode is to stop disconnecting, never to
 * disconnect a socket that is actually in use.
 */
function channelCount(): number {
  try {
    return realtime().channels?.length ?? -1;
  } catch {
    return -1;
  }
}

function socketIsUp(): boolean {
  try {
    const rt = realtime();
    return Boolean(rt.isConnected?.() || rt.isConnecting?.());
  } catch {
    return false;
  }
}

/**
 * One sample. Exported for tests — deterministic and side-effect free unless the
 * socket has genuinely been idle past the grace period.
 */
export function checkIdleSocketOnce(now: number = Date.now()): "disconnected" | "idle-waiting" | "in-use" | "skipped" {
  const channels = channelCount();

  // Unknown count, or channels still joined: the socket has work to do.
  if (channels !== 0) {
    zeroSince = null;
    return "in-use";
  }

  // A call being set up owns the socket even before its channel appears; the
  // same critical section forced reconnects respect (§13).
  if (isForcedReconnectSuppressed()) {
    zeroSince = null;
    return "skipped";
  }

  // Nothing joined AND nothing open — already in the desired state, and calling
  // disconnect() repeatedly would be pointless churn.
  if (!socketIsUp()) {
    zeroSince = null;
    return "skipped";
  }

  if (zeroSince === null) {
    zeroSince = now;
    return "idle-waiting";
  }
  if (now - zeroSince < IDLE_GRACE_MS) return "idle-waiting";

  zeroSince = null;
  try {
    console.info("Realtime: closing idle socket (no channels joined)", {
      idleMs: IDLE_GRACE_MS,
    });
    // disconnect() is async in realtime-js 2.110.x and resolves 'ok' | 'timeout'.
    // Nothing depends on the outcome — the next subscribe() re-opens the socket
    // regardless — so it is deliberately not awaited, only guarded.
    void Promise.resolve(realtime().disconnect?.(CLOSE_CODE_NORMAL, "idle: no channels")).catch(() => {
      /* best-effort teardown — never let it surface */
    });
  } catch {
    /* best-effort teardown — never let it surface */
  }
  return "disconnected";
}

/** Initialize once from App.tsx. Safe on web and native — uses no native APIs. */
export function initRealtimeIdleSocket(): void {
  if (timer !== null) return;
  timer = setInterval(() => {
    checkIdleSocketOnce();
  }, POLL_INTERVAL_MS);
}

/** Test seam / teardown. */
export function stopRealtimeIdleSocket(): void {
  if (timer !== null) {
    clearInterval(timer);
    timer = null;
  }
  zeroSince = null;
}

/** Test seam — resets the grace-period latch between cases. */
export function __resetIdleSocketForTests(): void {
  zeroSince = null;
}
